import { useState, useEffect } from 'react';
import {
  Button,
  Collapse,
  Row,
  Col,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';

import cx from 'classnames';

import CompareIcon from '../../../CompareIcon';

import CopyToClipboardIcon from 'components/CopyToClipboardIcon';
import { IcebergIcon } from '@tradesolution/iceberg-ui-react';

import EnhetApi from 'services/KjederegisteretAdminApi/EnhetApi';
import { LeverandorKundenummerDto } from 'services/KjederegisteretAdminApi/EnhetApi/types';

import openInNewTab from 'utils/functions/openInNewTab';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import {
  CompareStatus,
  qaExistInCommaSeparatedString,
} from 'pages/QA/components/QACardDetails/utils';

import Style from '../../index.module.css';
import { ChangeRequestValidator } from 'pages/QA/components/QACardDetails/ChangeRequestValidator';

interface Props {
  /**
   * The values that is displayed in the right input field.
   */
  values: string[];

  /**
   * Called when the right input field is changed.
   */
  onChange: (values: string[]) => void;

  /**
   * Used to check if other enheter are connected to the same leverandor kundenummer.
   */
  enhetId?: number;

  /**
   * List of leverandor kundenummer for the enhet.
   * Used to display other leverandor relations to the enhet
   */
  leverandorKundenummer?: LeverandorKundenummerDto[];
  changeRequest: {
    leverandorKundenummerHos: string;
    leverandorKundenummerHosTsKundeId: string;
    leverandorKundenummer: string;
  };
  isNedleggelse: boolean;
}

const KundenummerRow = (props: Props) => {
  const isMountedRef = useIsMountedRef();

  const [isExpandRow, setIsExpandRow] = useState<boolean>(false);
  const [improvedKundenummer, setImprovedKundenummer] = useState<string>(props.values.join(', '));
  const [compareStatus, setCompareStatus] = useState<CompareStatus>(
    qaExistInCommaSeparatedString(improvedKundenummer, props.changeRequest.leverandorKundenummer),
  );
  const [otherConnectedEnheter, setOtherConnectedEnheter] = useState<LeverandorKundenummerDto[]>(
    [],
  );
  const [crValueError, setCrValueError] = useState<string | null>(null);
  const [removedKundenummer, setRemovedKundenummer] = useState<string[]>([]);

  const loadOtherConnectedEnheter = async () => {
    if (
      props.changeRequest.leverandorKundenummerHosTsKundeId &&
      props.changeRequest.leverandorKundenummer
    ) {
      const result = await EnhetApi.getLeverandorKundenummer(
        props.changeRequest.leverandorKundenummerHosTsKundeId,
        props.changeRequest.leverandorKundenummer,
      );
      setOtherConnectedEnheter(
        result?.length > 0 ? result.filter((x) => x.enhetId !== props.enhetId) : [],
      );
    }
  };

  const transferKundeNr = (kundenr: string) => {
    // convert input to array
    const arr = parseInputToArray();

    // add input to array if it does not already exist
    if (arr.indexOf(kundenr) === -1) {
      arr.push(kundenr);
    }

    // update collection
    props.onChange(arr);
  };

  const handleKundeNrBlur = () => {
    const arr = parseInputToArray();
    props.onChange(arr);
  };

  //Sjekk duplikater for Kundenummer hos leverandør når isNedleggelse er true
  const checkAndRemoveDuplicateKundenummer = (inputValue: string) => {
    if (!props.isNedleggelse || !props.changeRequest.leverandorKundenummer) {
      return inputValue;
    }

    const leftSideNumbers = props.changeRequest.leverandorKundenummer
      .split(',')
      .map((num) => num.trim())
      .filter((num) => num);

    const rightSideNumbers = inputValue
      .split(',')
      .map((num) => num.trim())
      .filter((num) => num);

    const newRemovedNumbers: string[] = [];
    const filteredRightSide = rightSideNumbers.filter((rightNum) => {
      const isDuplicate = leftSideNumbers.includes(rightNum);
      if (isDuplicate) {
        newRemovedNumbers.push(rightNum);
        return false;
      }
      return true;
    });

    // oppdaterer listen med fjernede kundenummer
    setRemovedKundenummer((prev) => {
      const combined = [...prev, ...newRemovedNumbers];
      return Array.from(new Set(combined)); // Remove duplicates
    });

    return filteredRightSide.join(', ');
  };

  const parseInputToArray = () =>
    improvedKundenummer
      .split(',')
      .map((o) => o.trim())
      .filter((o) => o);

  useEffect(() => {
    if (compareStatus === 'ulik' || compareStatus === 'ny' || otherConnectedEnheter?.length > 0) {
      setIsExpandRow(true);
    }
  }, [compareStatus, otherConnectedEnheter]);

  useEffect(() => {
    setCompareStatus(
      qaExistInCommaSeparatedString(improvedKundenummer, props.changeRequest.leverandorKundenummer),
    );
  }, [improvedKundenummer, props.changeRequest.leverandorKundenummer]);

  useEffect(() => {
    loadOtherConnectedEnheter();
  }, [props.changeRequest.leverandorKundenummer]);

  useEffect(() => {
    if (isMountedRef.current) {
      const newValue = props.values.join(', ');
      const filteredValue = checkAndRemoveDuplicateKundenummer(newValue);
      setImprovedKundenummer(filteredValue);

      // If the filtered value is different from the original, update the parent
      if (filteredValue !== newValue) {
        const arr = filteredValue
          .split(',')
          .map((o) => o.trim())
          .filter((o) => o);
        props.onChange(arr);
      }
    }
  }, [props.values.join(', '), props.isNedleggelse, props.changeRequest.leverandorKundenummer]);

  const handleExpandRowClick = () => {
    setIsExpandRow(!isExpandRow);
  };

  // Check if the value from the change request has any errors
  useEffect(() => {
    if (props.changeRequest.leverandorKundenummer) {
      const error = ChangeRequestValidator.hasLeverandorKundenummerErrors(
        props.changeRequest.leverandorKundenummer,
      );
      setCrValueError(error);
    } else {
      setCrValueError(null);
    }
  }, [props.changeRequest.leverandorKundenummer]);

  // Clear removed numbers when isNedleggelse becomes false
  useEffect(() => {
    if (!props.isNedleggelse) {
      setRemovedKundenummer([]);
    }
  }, [props.isNedleggelse]);

  const formattedMottakerNavn = Object.entries(
    (props.leverandorKundenummer || [])
      .filter((x) => x.tsKundeId !== props.changeRequest.leverandorKundenummerHosTsKundeId)
      .reduce(
        (acc, curr) => {
          const name = curr.mottakerNavn;
          acc[name] = (acc[name] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      ),
  )
    .map(([name, count]) => `${name} (${count})`)
    .join(', ');

  return (
    <>
      <div className={Style.changeRequestRowHeaderContainer} onClick={handleExpandRowClick}>
        <h6 className={cx(Style.changeRequestRowHeader, Style.changeRequestRowHeaderGrid)}>
          <Row className="g-0">
            <Col xs={5} className="d-flex align-items-center gap-1">
              {!isExpandRow && <CompareIcon compareStatus={compareStatus} size="sm" />}
              <span className={Style.changeRequestRowHeaderText}>
                Kundenummer hos leverandør:{' '}
                <strong>{props.changeRequest.leverandorKundenummerHos}</strong>
              </span>
            </Col>
            <Col xs={2}></Col>
            <Col xs={4} className="d-flex align-items-center" style={{ minWidth: 0 }}>
              <div className="w-100" style={{ minWidth: 0 }}>
                {formattedMottakerNavn && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{formattedMottakerNavn}</Tooltip>}
                  >
                    <strong
                      style={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                        width: '100%',
                      }}
                    >
                      {formattedMottakerNavn}
                    </strong>
                  </OverlayTrigger>
                )}
              </div>
            </Col>
            <Col xs={1} className="d-flex align-items-center justify-content-end">
              <Button variant="link" size="sm">
                <IcebergIcon icon={isExpandRow ? 'chevron-up' : 'chevron-down'} bold />
              </Button>
            </Col>
          </Row>
        </h6>
      </div>
      <Collapse in={isExpandRow}>
        <div>
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    value={props.changeRequest.leverandorKundenummer || ''}
                    readOnly
                    isValid={!crValueError && !!props.changeRequest.leverandorKundenummer}
                    isInvalid={!!crValueError}
                  />
                  <InputGroup.Text>
                    <CopyToClipboardIcon val={props.changeRequest.leverandorKundenummer} />
                  </InputGroup.Text>
                </InputGroup>
                {otherConnectedEnheter?.length > 0 && (
                  <Form.Text>
                    <span style={{ color: 'red' }}>
                      Andre enheter koblet til dette kundenummeret:{' '}
                    </span>
                    <br />
                    {otherConnectedEnheter.map((x) => (
                      <div key={x.enhetId}>
                        <a href="#" onClick={(e) => openInNewTab(`/enheter/${x.enhetId}`, e)}>
                          {x.enhetNavn}
                        </a>
                      </div>
                    ))}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              {!!crValueError ? (
                <CompareIcon compareStatus="ugyldig" tooltipText={crValueError} />
              ) : (
                <CompareIcon
                  compareStatus={compareStatus}
                  onClick={() => transferKundeNr(props.changeRequest.leverandorKundenummer)}
                />
              )}
            </Col>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    className={Style.formControl}
                    type="text"
                    value={improvedKundenummer || ''}
                    onChange={(e) => {
                      const filteredValue = checkAndRemoveDuplicateKundenummer(e.target.value);
                      setImprovedKundenummer(filteredValue);
                    }}
                    onBlur={handleKundeNrBlur}
                  />
                </InputGroup>
                {removedKundenummer.length > 0 && (
                  <div>
                    <Form.Text className="text-warning">
                      Kundenummer {removedKundenummer.map((num) => `"${num}"`).join(', ')} er
                      fjernet
                    </Form.Text>
                  </div>
                )}
                {props.leverandorKundenummer?.filter(
                  (x) => x.tsKundeId !== props.changeRequest.leverandorKundenummerHosTsKundeId,
                ).length > 0 && (
                  <Form.Text>
                    Andre leverandører med kundeforhold til enhet: <br />
                    {props.leverandorKundenummer
                      .filter(
                        (x) =>
                          x.tsKundeId !== props.changeRequest.leverandorKundenummerHosTsKundeId,
                      )
                      .map((x) => (
                        <div key={x.id}>
                          {x.mottakerNavn}: {x.kundenummer}
                        </div>
                      ))}
                  </Form.Text>
                )}
                {props.leverandorKundenummer?.filter(
                  (x) => x.tsKundeId !== props.changeRequest.leverandorKundenummerHosTsKundeId,
                ).length === 0 && (
                  <Form.Text>Ingen andre leverandører med kundeforhold til enhet</Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>
      </Collapse>
    </>
  );
};

export default KundenummerRow;
